import { useState } from 'react';
import TimePickerField from './TimePickerField';
import CardGenerator from './CardGenerator';

const BAGHDAD_TZ = 'Asia/Baghdad';

const STATUS_FIELDS = [
  { key: 'printingCheck', label: 'طباعة البطاقة', dateKey: 'printingDate' },
  {
    key: 'AddEmailCheck',
    label: 'إضافة البريد',
    dateKey: 'AddEmailDate',
  },
  { key: 'paperCat', label: 'papercut', dateKey: 'paperCatDate' },
  { key: 'endCheck', label: 'الإكمال النهائي', dateKey: 'endCheckDate' },
];

const formatDate = (value) => {
  if (!value || value === 'false') {
    return null;
  }
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    return date.toLocaleDateString('ar-IQ', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: BAGHDAD_TZ,
    });
  } catch {
    return null;
  }
};

const formatDateTime = (value) => {
  if (!value || value === 'false') {
    return null;
  }
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    return date.toLocaleString('ar-IQ', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: BAGHDAD_TZ,
      hour12: false,
    });
  } catch {
    return null;
  }
};

function UsersList({
  users = [],
  canConfirmPrinting = false,
  onConfirmPrinting,
  confirmingId,
  canUpdateNfc = false,
  onUpdateNfc,
  updatingNfcId,
  canConfirmEmail = false,
  onConfirmEmail,
  confirmingEmailId,
  canConfirmPaperCat = false,
  onConfirmPaperCat,
  confirmingPaperCatId,
  canConfirmEndAll = false,
  onConfirmEndAll,
  confirmingEndAllId,
  onExportUser,
  exportTooltip = 'تصدير بيانات هذا المستخدم إلى Excel',
  schoolName = '',
  cardExpiryDate = null,
}) {
  const [customDates, setCustomDates] = useState({});
  const [emailDates, setEmailDates] = useState({});
  const [paperCatDates, setPaperCatDates] = useState({});
  const [endDates, setEndDates] = useState({});
  const [nfcInputs, setNfcInputs] = useState({});

  const handleDateChange = (userKey, field, value) => {
    setCustomDates((prev) => ({
      ...prev,
      [userKey]: {
        ...(prev[userKey] || {}),
        [field]: value,
      },
    }));
  };

  const parseIsoDateParts = (isoString) => {
    if (!isoString) {
      return null;
    }
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) {
      return null;
    }

    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: BAGHDAD_TZ,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    const parts = formatter.formatToParts(date).reduce((acc, part) => {
      if (part.type !== 'literal') {
        acc[part.type] = part.value;
      }
      return acc;
    }, {});

    return {
      date: `${parts.year}-${parts.month}-${parts.day}`,
      time: `${parts.hour}:${parts.minute}`,
    };
  };

  const getDatePart = (userKey, field, fallbackParts) => {
    if (customDates[userKey]?.[field]) {
      return customDates[userKey][field];
    }
    return fallbackParts?.[field] || '';
  };

  const getActionDatePart = (stateMap, userKey, field, fallbackParts) =>
    stateMap[userKey]?.[field] || fallbackParts?.[field] || '';

  const setActionDatePart = (setter, userKey, field, value) => {
    setter((prev) => ({
      ...prev,
      [userKey]: {
        ...(prev[userKey] || {}),
        [field]: value,
      },
    }));
  };

  const buildActionDateTimeValue = (stateMap, userKey, fallbackParts) => {
    const entry = stateMap[userKey] || fallbackParts;
    if (!entry?.date) {
      return '';
    }
    const timeValue =
      entry.time && entry.time.length > 0 ? entry.time : '00:00';
    return `${entry.date}T${timeValue}:00+03:00`;
  };

  const buildDateTimeValue = (userKey, fallbackParts) =>
    buildActionDateTimeValue(customDates, userKey, fallbackParts);

  const getUserKey = (user) =>
    user?._id || user?.id || user?.userId || user?.userIdNumber;

  const handleNfcChange = (userKey, value) => {
    setNfcInputs((prev) => ({
      ...prev,
      [userKey]: value,
    }));
  };

  const getNfcValue = (userKey, fallback) =>
    typeof nfcInputs[userKey] === 'string'
      ? nfcInputs[userKey]
      : fallback || '';

  if (!users || users.length === 0) {
    return <div className="empty-panel">لا يوجد مستخدمون لعرضهم.</div>;
  }

  const renderStatusTag = (label, value, dateValue) => {
    if (typeof value !== 'boolean') {
      return null;
    }
    const formattedDate =
      label === 'طباعة البطاقة' ? formatDateTime(dateValue) : formatDate(dateValue);
    return (
      <span className={`tag ${value ? 'positive' : 'neutral'}`}>
        {label}: {value ? 'مكتمل' : 'غير مكتمل'}
        {formattedDate && <small> • {formattedDate}</small>}
      </span>
    );
  };

  const getVisibleStatusFields = () => {
    if (canConfirmPrinting) {
      // لصلاحية الطباعة: إظهار طباعة البطاقة فقط
      return STATUS_FIELDS.filter((field) => field.key === 'printingCheck');
    }
    if (canConfirmEmail) {
      return STATUS_FIELDS.filter(
        (field) => !['paperCat', 'endCheck'].includes(field.key)
      );
    }
    if (canConfirmPaperCat) {
      return STATUS_FIELDS.filter((field) => field.key !== 'endCheck');
    }
    return STATUS_FIELDS;
  };

  return (
    <ul className="users-list">
      {users.map((user) => {
        const userKey = getUserKey(user);
        const initialParts = parseIsoDateParts(
          user?.printingDate || user?.printingCreatedAt
        );
        const addEmailInitialParts = parseIsoDateParts(
          user?.AddEmailDate || user?.AddEmailCreatedAt
        );
        const paperCatInitialParts = parseIsoDateParts(
          user?.paperCatDate || user?.paperCatCreatedAt
        );
        const endInitialParts = parseIsoDateParts(
          user?.endCheckDate || user?.endCheckCreatedAt
        );
        const emailInitialParts = parseIsoDateParts(
          user?.AddEmailDate || user?.AddEmailCreatedAt
        );
        const gender = parseInt(user?.userGender, 10);
        const providedImage =
          user?.userImage || user?.profileImage || user?.image || null;
        const avatarSrc =
          gender === 0
            ? '/default-female-avatar.png'
            : providedImage || '/default-male-avatar.jpg';
        return (
          <li key={userKey}>
          <div className="user-name-row">
            <div className="user-name" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div className="user-avatar-circle">
                <img 
                  src={avatarSrc}
                  alt={user?.userName || 'مستخدم'}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src =
                      gender === 0
                        ? '/default-female-avatar.png'
                        : '/default-male-avatar.jpg';
                  }}
                />
              </div>
              <span>
                {user?.userName ||
                  user?.fullName ||
                  user?.name ||
                  user?.userIdNumber}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {typeof onExportUser === 'function' && (
                <button
                  type="button"
                  className="icon-button"
                  onClick={() => onExportUser(user)}
                  title={exportTooltip}
                  aria-label={exportTooltip}
                >
                  📥
                </button>
              )}
              {canConfirmPrinting && (
                <CardGenerator 
                  user={user} 
                  schoolName={schoolName}
                  expiryDate={cardExpiryDate}
                />
              )}
            </div>
          </div>
          <div className="user-meta">
            {user?.userIdNumber && <span className="tag">المعرف: {user.userIdNumber}</span>}
            {(user?.role || user?.userRole || user?.jobTitle || user?.position || user?.userPosition || user?.job) && (
              <span className="tag role-tag">
                الصفة: {user.role || user.userRole || user.jobTitle || user.position || user.userPosition || user.job}
              </span>
            )}
            {user?.groupName && <span className="tag">المجموعة: {user.groupName}</span>}
            {getVisibleStatusFields().map(({ key, label, dateKey }) =>
              renderStatusTag(label, user?.[key], user?.[dateKey])
            )}
          </div>
          {canUpdateNfc && (
            <div className="user-actions">
              <div className="nfc-row">
                <label className="section-label">
                  <span>رمز الـ NFC:</span>
                  <input
                    type="text"
                    className="date-input"
                    value={getNfcValue(userKey, user?.nfcId)}
                    onChange={(event) => handleNfcChange(userKey, event.target.value)}
                    placeholder="أدخل رمز البطاقة"
                    style={{ width: '140px' }}
                  />
                </label>
                <button
                  type="button"
                  className="secondary"
                  disabled={
                    updatingNfcId === userKey ||
                    !getNfcValue(userKey, user?.nfcId)?.trim()
                  }
                  onClick={() =>
                    onUpdateNfc?.(user, getNfcValue(userKey, user?.nfcId))
                  }
                >
                  {updatingNfcId === userKey ? 'جارٍ التحديث...' : 'تحديث'}
                </button>
                {user?.nfcId && (
                  <span className="tag nfc-tag" style={{ margin: 0 }}>الحالي: {user.nfcId}</span>
                )}
              </div>
            </div>
          )}
          {canConfirmEmail && !canConfirmPrinting && (
            <div className="user-actions">
              <div className="date-time-row">
                <label>
                  تاريخ إضافة البريد
                  <input
                    type="date"
                    className="date-input"
                    value={getActionDatePart(
                      emailDates,
                      userKey,
                      'date',
                      addEmailInitialParts
                    )}
                    onChange={(event) =>
                      setActionDatePart(
                        setEmailDates,
                        userKey,
                        'date',
                        event.target.value
                      )
                    }
                  />
                </label>
                <label>
                  الوقت
                  <TimePickerField
                    value={
                      getActionDatePart(
                        emailDates,
                        userKey,
                        'time',
                        addEmailInitialParts
                      ) || null
                    }
                    onChange={(timeValue) =>
                      setActionDatePart(
                        setEmailDates,
                        userKey,
                        'time',
                        timeValue || ''
                      )
                    }
                  />
                </label>
              </div>
              <button
                type="button"
                className="primary"
                disabled={
                  confirmingEmailId === userKey ||
                  user?.AddEmailCheck ||
                  !buildActionDateTimeValue(
                    emailDates,
                    userKey,
                    addEmailInitialParts
                  )
                }
                onClick={() =>
                  onConfirmEmail?.(
                    user,
                    buildActionDateTimeValue(
                      emailDates,
                      userKey,
                      addEmailInitialParts
                    )
                  )
                }
              >
                {user?.AddEmailCheck
                  ? 'تم تأكيد البريد'
                  : confirmingEmailId === userKey
                  ? 'جارٍ التأكيد...'
                  : 'تأكيد إضافة البريد'}
              </button>
            </div>
          )}
          {canConfirmPaperCat && !canConfirmPrinting && (
            <div className="user-actions">
              <div className="date-time-row">
                <label>
                  تاريخ papercut
                  <input
                    type="date"
                    className="date-input"
                    value={getActionDatePart(
                      paperCatDates,
                      userKey,
                      'date',
                      paperCatInitialParts
                    )}
                    onChange={(event) =>
                      setActionDatePart(
                        setPaperCatDates,
                        userKey,
                        'date',
                        event.target.value
                      )
                    }
                  />
                </label>
                <label>
                  الوقت
                  <TimePickerField
                    value={
                      getActionDatePart(
                        paperCatDates,
                        userKey,
                        'time',
                        paperCatInitialParts
                      ) || null
                    }
                    onChange={(timeValue) =>
                      setActionDatePart(
                        setPaperCatDates,
                        userKey,
                        'time',
                        timeValue || ''
                      )
                    }
                  />
                </label>
              </div>
              <button
                type="button"
                className="primary"
                disabled={
                  confirmingPaperCatId === userKey ||
                  user?.paperCat ||
                  !buildActionDateTimeValue(
                    paperCatDates,
                    userKey,
                    paperCatInitialParts
                  )
                }
                onClick={() =>
                  onConfirmPaperCat?.(
                    user,
                    buildActionDateTimeValue(
                      paperCatDates,
                      userKey,
                      paperCatInitialParts
                    )
                  )
                }
              >
                {user?.paperCat
                  ? 'تم تأكيد papercut'
                  : confirmingPaperCatId === userKey
                  ? 'جارٍ التأكيد...'
                  : 'تأكيد papercut'}
              </button>
            </div>
          )}
          {canConfirmEndAll && !canConfirmPrinting && (
            <div className="user-actions">
              <div className="date-time-row">
                <label>
                  تاريخ الإكمال النهائي
                  <input
                    type="date"
                    className="date-input"
                    value={getActionDatePart(
                      endDates,
                      userKey,
                      'date',
                      endInitialParts
                    )}
                    onChange={(event) =>
                      setActionDatePart(
                        setEndDates,
                        userKey,
                        'date',
                        event.target.value
                      )
                    }
                  />
                </label>
                <label>
                  الوقت
                  <TimePickerField
                    value={
                      getActionDatePart(
                        endDates,
                        userKey,
                        'time',
                        endInitialParts
                      ) || null
                    }
                    onChange={(timeValue) =>
                      setActionDatePart(
                        setEndDates,
                        userKey,
                        'time',
                        timeValue || ''
                      )
                    }
                  />
                </label>
              </div>
              <button
                type="button"
                className="primary"
                disabled={
                  confirmingEndAllId === userKey ||
                  user?.endCheck ||
                  !buildActionDateTimeValue(endDates, userKey, endInitialParts)
                }
                onClick={() =>
                  onConfirmEndAll?.(
                    user,
                    buildActionDateTimeValue(endDates, userKey, endInitialParts)
                  )
                }
              >
                {user?.endCheck
                  ? 'تم تأكيد الإكمال النهائي'
                  : confirmingEndAllId === userKey
                  ? 'جارٍ التأكيد...'
                  : 'تأكيد الإكمال النهائي'}
              </button>
            </div>
          )}
          {canConfirmPrinting && (
            <div className="user-actions">
              <div className="date-time-row">
                <label className="section-label">
                  <span>تاريخ الطباعة:</span>
                  <input
                    type="date"
                    className="date-input"
                    value={getDatePart(userKey, 'date', initialParts)}
                    onChange={(event) =>
                      handleDateChange(userKey, 'date', event.target.value)
                    }
                  />
                </label>
                <label className="section-label">
                  <span>الوقت:</span>
                  <TimePickerField
                    value={getDatePart(userKey, 'time', initialParts) || null}
                    onChange={(timeValue) =>
                      handleDateChange(userKey, 'time', timeValue || '')
                    }
                  />
                </label>
              </div>
              <button
                type="button"
                className="primary"
                disabled={
                  confirmingId === userKey ||
                  user?.printingCheck ||
                  !buildDateTimeValue(userKey, initialParts) ||
                  !getNfcValue(userKey, user?.nfcId)?.trim()
                }
                onClick={() =>
                  onConfirmPrinting?.(user, buildDateTimeValue(userKey, initialParts))
                }
              >
                {user?.printingCheck
                  ? 'تم تأكيد الطباعة'
                  : confirmingId === userKey
                  ? 'جارٍ التأكيد...'
                  : 'تأكيد طباعة البطاقة'}
              </button>
            </div>
          )}
        </li>
        );
      })}
    </ul>
  );
}

export default UsersList;

